import { useEffect, useMemo, useRef, useState } from 'react';
import type { StreamEvent, Platform } from '@hypevault/shared-types';
import {
  DEFAULT_MESSAGES,
  EVENT_BADGES,
  PLATFORM_COLORS,
  derivePalette,
  eventKeyCascade,
  extractSubMessage,
  extractTemplateVars,
  extractUsername,
  passesMinFilters,
  pickFrom,
  renderTemplate,
} from '../alertHelpers';

/**
 * Stream alerts overlay — v2 port of v1's alert.ejs + stream-alerts-banner.
 *
 * Config (all optional):
 *   variant: 'standard' | 'banner'                          (default 'standard')
 *   duration: seconds                                       (default 6)
 *   enabledEvents: string[]   subset of event-type tokens   (default all)
 *   accentColor: string       override the platform color   (default per-platform)
 *   platformColors: per-platform hex overrides              (default built-in)
 *   minTipCoins, minCheerBits, minSuperChatCents            (default 0)
 *   soundEnabled: boolean                                   (default true)
 *   soundUrl: string                                        (default '/sounds/banner-alert.wav')
 *   messageTemplates: Record<string,string>                 (per-event-token override)
 *   customImage: string       (replaces the badge icon)
 */

type Variant = 'standard' | 'banner';

interface QueueItem {
  id: string;
  eventToken: string;
  badge: string;
  platform: Platform;
  username: string;
  message: string;
  /** Viewer-supplied note (tip message, cheer comment, SuperChat text). */
  subMessage: string | null;
  customImage: string | null;
  duration: number;
}

export function AlertBoxWidget({
  fields,
  latestEvent,
}: {
  fields: Record<string, unknown>;
  latestEvent: StreamEvent | null;
}) {
  const variant: Variant = (fields.variant as Variant) ?? 'standard';
  const durationMs = Number(fields.duration ?? 6) * 1000;
  const enabledEvents = useMemo(
    () => new Set((fields.enabledEvents as string[] | null | undefined) ?? []),
    [fields.enabledEvents],
  );
  const hasFilter = (fields.enabledEvents as string[] | undefined)?.length;
  const minTip = Number(fields.minTipCoins ?? 0);
  const minCheer = Number(fields.minCheerBits ?? 0);
  const minSuper = Number(fields.minSuperChatCents ?? 0);
  const soundEnabled = (fields.soundEnabled as boolean | undefined) ?? true;
  const soundUrl = (fields.soundUrl as string | undefined) ?? null;
  const accentOverride = (fields.accentColor as string | undefined) ?? null;
  const platformColors = (fields.platformColors as Record<string, string> | undefined) ?? {};
  const messageTemplates = (fields.messageTemplates as Record<string, string> | undefined) ?? {};
  const customImage = (fields.customImage as string | undefined) ?? null;

  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [current, setCurrent] = useState<QueueItem | null>(null);
  const [phase, setPhase] = useState<'in' | 'out'>('in');
  const lastEventId = useRef<string | null>(null);

  // Enqueue when new event arrives
  useEffect(() => {
    if (!latestEvent || latestEvent.id === lastEventId.current) return;
    lastEventId.current = latestEvent.id;

    // Card redemptions render through the dedicated card-alert widget —
    // skip them here so a streamer running both doesn't get double alerts.
    if (latestEvent.type === 'cardRedemption') return;

    const cascade = eventKeyCascade(latestEvent);
    const token = cascade[0]!;
    if (hasFilter && !cascade.some((k) => enabledEvents.has(k))) return;
    if (!passesMinFilters(latestEvent, minTip, minCheer, minSuper)) return;

    const vars = extractTemplateVars(latestEvent);
    const tpl = pickFrom(cascade, messageTemplates, DEFAULT_MESSAGES) ?? '';
    setQueue((q) => [
      ...q,
      {
        id: latestEvent.id,
        eventToken: token,
        badge: pickFrom(cascade, EVENT_BADGES) ?? '🔔',
        platform: latestEvent.platform,
        username: extractUsername(latestEvent),
        message: renderTemplate(tpl, vars),
        subMessage: extractSubMessage(latestEvent),
        customImage,
        duration: durationMs,
      },
    ]);
  }, [
    latestEvent,
    enabledEvents,
    hasFilter,
    minTip,
    minCheer,
    minSuper,
    durationMs,
    customImage,
    messageTemplates,
  ]);

  // Drive queue → current. Only takes the next item; does NOT schedule the
  // fade-out (that's the next effect). Keeping them separate prevents the
  // setCurrent() inside this effect from re-triggering its own cleanup and
  // clearing the fade timers before they fire.
  useEffect(() => {
    if (current || queue.length === 0) return;
    const next = queue[0]!;
    setQueue((q) => q.slice(1));
    setCurrent(next);
    setPhase('in');
    if (soundEnabled) {
      const url = soundUrl ?? '/sounds/banner-alert.wav';
      try {
        const audio = new Audio(url);
        audio.volume = 0.5;
        void audio.play().catch(() => {});
      } catch {
        /* ignore */
      }
    }
  }, [current, queue, soundEnabled, soundUrl]);

  // Schedule fade-out whenever a new `current` mounts. Cleanup only runs when
  // `current` actually changes (either to null after fade, or to the next
  // queued item) — by then the timers have already fired.
  useEffect(() => {
    if (!current) return;
    const tIn = setTimeout(() => setPhase('out'), current.duration - 500);
    const tOut = setTimeout(() => setCurrent(null), current.duration);
    return () => {
      clearTimeout(tIn);
      clearTimeout(tOut);
    };
  }, [current]);

  if (!current) return null;

  // Priority for the palette:
  //   1. fields.accentColor — legacy single-color override across all platforms
  //   2. fields.platformColors[platform] — per-platform override (derived light/dark/dim)
  //   3. PLATFORM_COLORS[platform] — built-in defaults
  const platformOverride = platformColors[current.platform];
  const colors = platformOverride
    ? derivePalette(platformOverride)
    : PLATFORM_COLORS[current.platform];
  const accent = accentOverride ?? colors.base;
  const cssVars: React.CSSProperties = {
    ['--alert-color' as string]: accent,
    ['--alert-color-light' as string]: colors.light,
    ['--alert-color-dark' as string]: colors.dark,
    ['--alert-color-dim' as string]: colors.dim,
  };

  return (
    <div className={`alert-root variant-${variant} phase-${phase}`} style={cssVars}>
      {variant === 'banner' ? <BannerCard item={current} /> : <StandardCard item={current} />}
      <AlertStyles />
    </div>
  );
}

function StandardCard({ item }: { item: QueueItem }) {
  const badge = item.badge;
  const iconSize = 140;
  const fontUser = 30;
  const fontMsg = 16;
  return (
    <div className="alert-card">
      <div className="particles">
        {Array.from({ length: 12 }).map((_, i) => (
          <span key={i} className={`particle p-${i}`} />
        ))}
      </div>
      <div className="glow-ring" style={{ width: iconSize + 40, height: iconSize + 40 }} />
      <div className="spin-ring" style={{ width: iconSize + 20, height: iconSize + 20 }} />
      <div
        className="icon-circle"
        style={{ width: iconSize, height: iconSize, fontSize: iconSize * 0.42 }}
      >
        {item.customImage ? (
          <img alt="" src={item.customImage} className="custom-art" />
        ) : (
          <span>{badge}</span>
        )}
        <span className="shimmer" />
        <span className="event-badge">{badge}</span>
      </div>
      <div className="alert-username" style={{ fontSize: fontUser }}>
        {item.username}
      </div>
      <div className="alert-message" style={{ fontSize: fontMsg }}>
        {item.message}
      </div>
      {item.subMessage && (
        <div className="alert-submessage" style={{ fontSize: fontMsg }}>
          “{item.subMessage}”
        </div>
      )}
      <div className="accent-line" />
    </div>
  );
}

function BannerCard({ item }: { item: QueueItem }) {
  const badge = item.badge;
  return (
    <div className="banner-card">
      <div className="banner-bg" />
      <div className="banner-inner">
        <div className="banner-icon">
          {item.customImage ? <img alt="" src={item.customImage} className="custom-art" /> : badge}
        </div>
        <div className="banner-text">
          <div className="banner-username">{item.username}</div>
          <div className="banner-message">{item.message}</div>
          {item.subMessage && <div className="banner-submessage">“{item.subMessage}”</div>}
        </div>
      </div>
    </div>
  );
}

function AlertStyles() {
  return (
    <style>{`
      .alert-root {
        width: 100%;
        height: 100%;
        position: relative;
        font-family: 'Inter', system-ui, sans-serif;
        pointer-events: none;
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .alert-root.variant-banner {
        align-items: flex-start;
        justify-content: stretch;
      }

      /* Standard / compact */
      .alert-card {
        position: relative;
        width: 100%;
        max-width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        opacity: 0;
        transform: scale(0.3);
      }
      .phase-in .alert-card { animation: ah-card-in 0.7s cubic-bezier(0.16,1,0.3,1) forwards; }
      .phase-out .alert-card { animation: ah-card-out 0.5s ease-in forwards; }
      @keyframes ah-card-in {
        0% { opacity: 0; transform: scale(0.3) rotate(-5deg); }
        50% { opacity: 1; transform: scale(1.08) rotate(1deg); }
        70% { transform: scale(0.96) rotate(-0.5deg); }
        100% { opacity: 1; transform: scale(1) rotate(0); }
      }
      @keyframes ah-card-out {
        0% { opacity: 1; transform: scale(1); }
        100% { opacity: 0; transform: scale(0.6) translateY(-30px); filter: blur(4px); }
      }
      .glow-ring {
        position: absolute;
        border-radius: 50%;
        top: -10px;
        opacity: 0;
        pointer-events: none;
      }
      .phase-in .glow-ring { animation: ah-ring 2s ease-out forwards; }
      @keyframes ah-ring {
        0% { opacity: 0; box-shadow: 0 0 0 0 var(--alert-color); transform: scale(0.5); }
        20% { opacity: 0.8; transform: scale(1); box-shadow: 0 0 40px 8px var(--alert-color), 0 0 80px 16px var(--alert-color-dim); }
        100% { opacity: 0; transform: scale(1.6); }
      }
      .spin-ring {
        position: absolute;
        border-radius: 50%;
        border: 2px dashed var(--alert-color-dim);
        top: 0;
        opacity: 0;
      }
      .phase-in .spin-ring { animation: ah-spin-in 0.6s 0.2s ease-out forwards, ah-spin-rotate 8s 0.8s linear infinite; }
      @keyframes ah-spin-in { to { opacity: 0.4; transform: rotate(45deg); } }
      @keyframes ah-spin-rotate { from { transform: rotate(45deg); } to { transform: rotate(405deg); } }

      .icon-circle {
        position: relative;
        z-index: 2;
        border-radius: 50%;
        background: radial-gradient(circle at 30% 30%, var(--alert-color-light), var(--alert-color-dark));
        border: 3px solid rgba(255,255,255,0.25);
        box-shadow: 0 0 30px var(--alert-color-dim), 0 8px 32px rgba(0,0,0,0.4), inset 0 -4px 12px rgba(0,0,0,0.2);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        overflow: hidden;
      }
      .phase-in .icon-circle { animation: ah-icon-bounce 0.8s 0.3s cubic-bezier(0.34,1.56,0.64,1) both; }
      @keyframes ah-icon-bounce {
        0% { transform: scale(0); }
        60% { transform: scale(1.15); }
        80% { transform: scale(0.92); }
        100% { transform: scale(1); }
      }
      .custom-art { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; }
      .event-badge {
        position: absolute;
        bottom: -4px;
        right: -4px;
        width: 34px;
        height: 34px;
        border-radius: 50%;
        background: #1e1b2e;
        border: 2px solid var(--alert-color);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        z-index: 3;
        box-shadow: 0 2px 8px rgba(0,0,0,0.5);
      }
      .phase-in .event-badge { animation: ah-badge-pop 0.4s 0.6s cubic-bezier(0.34,1.56,0.64,1) both; }
      @keyframes ah-badge-pop { from { transform: scale(0); } to { transform: scale(1); } }
      .shimmer {
        position: absolute;
        top: 0; left: -100%;
        width: 100%; height: 100%;
        background: linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent);
        z-index: 5;
        pointer-events: none;
      }
      .phase-in .shimmer { animation: ah-shimmer 1.5s 0.5s ease-in-out forwards; }
      @keyframes ah-shimmer { 0% { left: -100%; } 100% { left: 200%; } }

      .alert-username {
        margin-top: 14px;
        font-weight: 900;
        color: #fff;
        letter-spacing: -0.5px;
        text-shadow: 0 0 20px var(--alert-color-dim), 0 2px 4px rgba(0,0,0,0.5);
        z-index: 2;
        max-width: 92%;
        padding: 0 8px;
        word-break: break-word;
        line-height: 1.1;
      }
      .phase-in .alert-username { animation: ah-text 0.5s 0.4s ease-out both; }
      .alert-message {
        margin-top: 4px;
        font-weight: 600;
        color: var(--alert-color);
        z-index: 2;
        max-width: 92%;
        padding: 0 8px;
        word-break: break-word;
        line-height: 1.2;
      }
      .phase-in .alert-message { animation: ah-text 0.5s 0.5s ease-out both; }
      @keyframes ah-text { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      .alert-submessage {
        margin-top: 6px;
        font-style: italic;
        font-weight: 500;
        color: rgba(255, 255, 255, 0.78);
        z-index: 2;
        max-width: 92%;
        padding: 0 8px;
        word-break: break-word;
        line-height: 1.25;
      }
      .phase-in .alert-submessage { animation: ah-text 0.5s 0.7s ease-out both; }
      .accent-line {
        width: 0;
        height: 3px;
        border-radius: 2px;
        background: linear-gradient(90deg, transparent, var(--alert-color), transparent);
        margin-top: 10px;
        z-index: 2;
      }
      .phase-in .accent-line { animation: ah-line 0.6s 0.55s ease-out forwards; }
      @keyframes ah-line { from { width: 0; opacity: 0; } to { width: 140px; opacity: 0.7; } }

      .particles { position: absolute; inset: 0; pointer-events: none; z-index: 0; }
      .particle {
        position: absolute;
        width: 6px; height: 6px;
        border-radius: 50%;
        background: var(--alert-color);
        opacity: 0;
        top: 50%; left: 50%;
      }
      .phase-in .particle { animation: ah-particle 1.2s ease-out forwards; }
      @keyframes ah-particle {
        0% { opacity: 1; transform: translate(0,0) scale(1); }
        100% { opacity: 0; transform: translate(var(--px), var(--py)) scale(0); }
      }
      .p-0  { --px: -90px;  --py: -80px;  animation-delay: 0.10s; }
      .p-1  { --px: 100px;  --py: -60px;  animation-delay: 0.15s; }
      .p-2  { --px: -70px;  --py: 90px;   animation-delay: 0.08s; }
      .p-3  { --px: 80px;   --py: 100px;  animation-delay: 0.20s; }
      .p-4  { --px: -110px; --py: 10px;   animation-delay: 0.12s; }
      .p-5  { --px: 120px;  --py: -20px;  animation-delay: 0.18s; }
      .p-6  { --px: -30px;  --py: -110px; animation-delay: 0.05s; }
      .p-7  { --px: 40px;   --py: 110px;  animation-delay: 0.22s; }
      .p-8  { --px: -100px; --py: -40px;  animation-delay: 0.14s; width: 4px; height: 4px; }
      .p-9  { --px: 60px;   --py: -100px; animation-delay: 0.09s; width: 4px; height: 4px; }
      .p-10 { --px: -50px;  --py: 70px;   animation-delay: 0.25s; width: 8px; height: 8px; }
      .p-11 { --px: 110px;  --py: 40px;   animation-delay: 0.16s; width: 8px; height: 8px; }

      /* Banner — slides down from the top of the overlay as a horizontal strip */
      .banner-card {
        position: relative;
        width: 96%;
        max-width: 96%;
        margin: 8px auto 0;
        display: flex;
        align-items: center;
        gap: 20px;
        padding: 16px 28px;
        background: linear-gradient(
          90deg,
          var(--alert-color-dark) 0%,
          var(--alert-color) 55%,
          var(--alert-color-dark) 100%
        );
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-radius: 18px;
        box-shadow:
          0 12px 36px -10px rgba(0, 0, 0, 0.55),
          0 0 60px -10px var(--alert-color-dim);
        transform: translateY(-130%);
        will-change: transform;
        overflow: hidden;
      }
      .banner-card::before {
        content: '';
        position: absolute;
        inset: 0;
        background:
          radial-gradient(circle at 12% 50%, rgba(255, 255, 255, 0.18), transparent 45%),
          radial-gradient(circle at 88% 50%, rgba(255, 255, 255, 0.12), transparent 50%);
        pointer-events: none;
      }
      .phase-in .banner-card {
        animation: ah-banner-drop 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards;
      }
      .phase-out .banner-card {
        animation: ah-banner-retract 0.45s cubic-bezier(0.55, 0, 0.4, 1) forwards;
      }
      @keyframes ah-banner-drop {
        0%   { transform: translateY(-130%); }
        80%  { transform: translateY(4%); }
        100% { transform: translateY(0); }
      }
      @keyframes ah-banner-retract {
        0%   { transform: translateY(0); }
        100% { transform: translateY(-130%); }
      }
      .banner-inner {
        position: relative;
        z-index: 2;
        display: flex;
        align-items: center;
        gap: 18px;
        width: 100%;
        min-width: 0;
      }
      .banner-icon {
        flex: 0 0 auto;
        width: clamp(48px, 22%, 96px);
        aspect-ratio: 1 / 1;
        border-radius: 50%;
        background: radial-gradient(circle at 30% 30%, rgba(255,255,255,0.35), rgba(0,0,0,0.15));
        border: 2px solid rgba(255, 255, 255, 0.55);
        box-shadow:
          0 0 24px rgba(0, 0, 0, 0.35),
          inset 0 -4px 12px rgba(0, 0, 0, 0.25);
        display: grid;
        place-items: center;
        font-size: clamp(28px, 14%, 56px);
        color: white;
        overflow: hidden;
      }
      .banner-text {
        color: white;
        min-width: 0;
        flex: 1;
        display: flex;
        flex-direction: column;
        justify-content: center;
        line-height: 1.1;
      }
      .banner-username {
        font-size: clamp(20px, 4.5vw, 42px);
        font-weight: 900;
        letter-spacing: -0.5px;
        text-shadow: 0 2px 10px rgba(0, 0, 0, 0.45);
        word-break: break-word;
      }
      .banner-message {
        font-size: clamp(13px, 2.8vw, 22px);
        font-weight: 600;
        color: rgba(255, 255, 255, 0.92);
        margin-top: 4px;
        text-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);
        word-break: break-word;
      }
      .banner-submessage {
        font-size: clamp(12px, 2.4vw, 18px);
        font-style: italic;
        font-weight: 500;
        color: rgba(255, 255, 255, 0.78);
        margin-top: 4px;
        text-shadow: 0 1px 3px rgba(0, 0, 0, 0.45);
        word-break: break-word;
      }

    `}</style>
  );
}
